import cx from 'classnames'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { FontPairingSwatch } from 'components/FontPairingSwatch/FontPairingSwatch'
import { FontPairing } from 'constants/fontPairings'
import Styles from './TextStylesFieldDrawer.module.scss'
import { isEqual } from 'lodash'
import { forwardRef, useEffect, useRef, useState } from 'react'
import useButtonWithScrollCancel from 'hooks/useButtonWithScrollCancel'
import { useObjectRef } from '@react-aria/utils'
import scrollIntoView from 'scroll-into-view-if-needed'
import { Ref } from 'components/DesignContentPreview/Types'
import { zoomFrameIntoView } from 'components/Wizards/MobileWizards/utils/zoomFrameIntoView'

interface Props extends DrawerProps {
  initialValue: FontPairing
  onSelect: (pairing: FontPairing) => void
  pairings: FontPairingWithLabel[]
  editorRef?: Ref
}

interface SwatchProp {
  isSelected: boolean
  onSelect: (pairing: FontPairing) => void
  pairing: FontPairing
  label?: string
}

export type FontPairingWithLabel = { label?: string; fonts: FontPairing }

const Swatch = forwardRef<HTMLDivElement, SwatchProp>(({ label, pairing, isSelected, onSelect }, fRef) => {
  const ref = useObjectRef(fRef)

  const { buttonProps } = useButtonWithScrollCancel(
    {
      onPress: () => {
        onSelect(pairing)
      },
      elementType: 'div',
    },
    ref,
  )

  useEffect(() => {
    if (isSelected && ref.current) {
      scrollIntoView(ref.current, {
        scrollMode: 'if-needed',
        block: 'center',
        inline: 'center',
      })
    }
  }, [isSelected]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cx(Styles.swatch, { [Styles.selected]: isSelected })} {...buttonProps} ref={ref}>
      {label && <div className={Styles.swatchLabel}>{label}</div>}
      <FontPairingSwatch pairing={pairing} />
    </div>
  )
})

const SNAP_POINT = 0.5

export const TextStylesFieldDrawer = ({ pairings, initialValue, onSelect, editorRef, open, ...props }: Props) => {
  const [value, setValue] = useState(initialValue)
  const initialRender = useRef(true)

  const handleSelect = (pairing: FontPairing) => {
    setValue(pairing)
    onSelect(pairing)
  }

  useEffect(() => {
    if (!open) {
      editorRef?.setCameraToCurrentFrame({ animate: true, isMobile: true })
    }
  }, [open, editorRef]) // eslint-disable-line react-hooks/exhaustive-deps

  if (initialRender.current && editorRef) {
    zoomFrameIntoView(editorRef, SNAP_POINT)
    initialRender.current = false
  }

  return (
    <Drawer.Root {...props} activeSnapPoint={SNAP_POINT} snapPoints={[SNAP_POINT]} open={open}>
      <Drawer.Portal>
        <div
          style={{
            pointerEvents: 'all',
            inset: 0,
            position: 'fixed',
            zIndex: 9999,
          }}
        />
        <Drawer.Content fullHeight color="var(--light-100)">
          <div data-vaul-no-drag={true}>
            <Drawer.InnerContent>
              <Drawer.Top>
                <div />
                <Drawer.Title asChild>
                  <Heading lineClamp={1} level={5}>
                    Switch Fonts
                  </Heading>
                </Drawer.Title>
                <Drawer.Close label="Close" variant="ghost" position="right" />
                <Drawer.Description />
              </Drawer.Top>

              <Drawer.ScrollableContainer snap={0.5}>
                <div className={Styles.grid}>
                  {pairings.map((pairing, index) => (
                    <Swatch
                      key={index}
                      label={pairing.label}
                      pairing={pairing.fonts}
                      isSelected={isEqual(value, pairing.fonts)}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              </Drawer.ScrollableContainer>
            </Drawer.InnerContent>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
